import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { CostApi, Cost, NewCost } from '../models';
import { mapCostApiToCost } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { RootState } from '@/store/store';

import { API_FLEET_URL } from '@/utils';

export interface CostFilter {
  costModeId: string;
}

const costService = createApi({
  reducerPath: 'costsService',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_FLEET_URL}/costs`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;

      const token = state.auth?.auth?.accessToken || null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Cost', 'Costs'],
  endpoints: (builder) => ({
    getCost: builder.query<Cost, { costId: string }>({
      query: ({ costId }) => ({
        url: `/${costId}`,
      }),
      transformResponse: (response: CostApi) => mapCostApiToCost(response),
      providesTags: ['Cost'],
    }),
    getCosts: builder.query<
      PaginatedResponse<Cost>,
      { filters: CostFilter; paginated: PaginatedRequest }
    >({
      query: ({ filters, paginated }) => ({
        url: '',
        params: {
          ...filters,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<CostApi>) => ({
        ...response,
        items: response.items.map(mapCostApiToCost),
      }),
      providesTags: ['Costs'],
    }),
    createCost: builder.mutation<Cost, NewCost>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: { ...data },
      }),
      invalidatesTags: ['Costs'],
    }),
    updateCost: builder.mutation<
      Cost,
      { costId: string; data: Partial<NewCost> }
    >({
      query: ({ costId, data }) => ({
        url: `/${costId}`,
        method: 'PUT',
        body: { ...data },
      }),
      invalidatesTags: ['Cost', 'Costs'],
    }),
    deleteCost: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cost', 'Costs'],
    }),
  }),
});

export default costService;

export const {
  useGetCostQuery,
  useGetCostsQuery,
  useCreateCostMutation,
  useUpdateCostMutation,
  useDeleteCostMutation,
} = costService;
